import React, { useState } from 'react';
import { useTheme } from '../../../contexts/ThemeContext';
import PersonaBadge from '../../common/PersonaBadge';
import { FiChevronDown, FiChevronRight } from 'react-icons/fi';

interface Props {
  phases: any[];
}

const PhaseBreakdown: React.FC<Props> = ({ phases }) => {
  const { darkMode } = useTheme();
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const toggle = (id: string) => {
    setExpanded(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  if (phases.length === 0) {
    return (
      <div className={`p-8 text-center rounded-lg border ${darkMode ? 'border-gray-700 text-gray-500' : 'border-gray-200 text-gray-400'}`}>
        No phases defined in this workflow.
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {phases.map((phase, i) => {
        const isOpen = expanded.has(phase.id);
        const personas = phase.config?.personas || [];
        return (
          <div
            key={phase.id}
            className={`rounded-lg border overflow-hidden ${darkMode ? 'bg-gray-800/50 border-gray-700' : 'bg-white border-gray-200'}`}
          >
            <button
              onClick={() => toggle(phase.id)}
              className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${
                darkMode ? 'hover:bg-gray-700/50' : 'hover:bg-gray-50'
              }`}
            >
              {isOpen ? <FiChevronDown className="w-4 h-4 shrink-0" /> : <FiChevronRight className="w-4 h-4 shrink-0" />}
              <span className={`text-sm font-medium flex-1 ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                {i + 1}. {phase.label || phase.id}
              </span>
              <span className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                {phase.type || 'phase'} · {personas.length} persona{personas.length !== 1 ? 's' : ''}
              </span>
            </button>

            {isOpen && (
              <div className={`px-4 pb-4 pt-1 border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                {phase.config?.mission && (
                  <p className={`text-sm mb-3 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    <strong>Mission:</strong> {phase.config.mission}
                  </p>
                )}
                {phase.config?.objectives?.length > 0 && (
                  <div className="mb-3">
                    <p className={`text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Objectives:</p>
                    <ul className={`text-sm list-disc list-inside ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      {phase.config.objectives.map((obj: string, j: number) => (
                        <li key={j}>{obj}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {personas.length > 0 && (
                  <div>
                    <p className={`text-xs font-medium mb-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Assigned Personas:</p>
                    <div className="flex flex-wrap gap-2">
                      {personas.map((p: any) => (
                        <div key={p.persona_id} className="flex items-center gap-1">
                          <PersonaBadge personaId={p.persona_id} personaName={p.persona_name || p.persona_id} size="sm" />
                          <span className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>({p.role || 'Contributor'})</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default PhaseBreakdown;
